import { createFileRoute } from "@tanstack/react-router"
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Grid,
  Divider,
  Anchor,
  Avatar,
  useMantineColorScheme,
  Alert,
  Box,
} from "@mantine/core"
import {
  IconArrowLeft,
  IconPhone,
  IconMail,
  IconExternalLink,
  IconBrandTwitter,
  IconCalendar,
  IconQuote,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconInfoCircle,
  IconAlertTriangle,
  IconShieldCheck,
  IconQuestionMark,
} from "@tabler/icons-react"
import { allCongressMembers } from "@/data/congress-full-data"
import type { Position, TimelineEvent } from "@/types/congress"
import { MemberTimeline } from "@/components/congress/MemberTimeline"
import { SEO } from "@/components/seo/SEO"
import { StructuredData, createPersonSchema } from "@/components/seo/StructuredData"

export const Route = createFileRoute("/congress/senate/$state/$lastName")({
  component: SenatorProfilePage,
})

function generateSenatorTimeline(senator: { id: string; lastName: string; position: string }): TimelineEvent[] {
  const events: TimelineEvent[] = []
  const lastName = senator.lastName.toLowerCase()

  // Base vote event for all senators - ALL senators voted on this amendment
  const voteTitle =
    senator.position === "support" ? "Voted FOR Epstein File Transparency" : "Voted to BLOCK File Release"

  const voteDescription =
    senator.position === "support"
      ? `Voted FOR the Schumer Amendment (S.Amdt.3849) to compel DOJ release of all Jeffrey Epstein files. Showed political courage by supporting full transparency despite party pressure. The amendment was blocked 51-49 by those protecting the files.`
      : `Voted AGAINST the Schumer Amendment (S.Amdt.3849) to compel DOJ release of all Jeffrey Epstein files. Helped block transparency measure 51-49, keeping files hidden from public. Constituents deserve to know why they voted to protect these files from release.`

  events.push({
    id: `${senator.id}-vote-schumer-amendment-2025-09-10`,
    date: "2025-09-10",
    type: "vote",
    title: voteTitle,
    description: voteDescription,
    source: "Senate Roll Call Vote",
    url: "https://www.senate.gov/legislative/LIS/roll_call_votes/vote1191/vote_119_1_00512.htm",
    sentiment: senator.position === "support" ? "support" : "oppose",
    verified: true,
    importance: "high",
    tags: senator.position === "support" ? ["vote", "transparency", "courage"] : ["vote", "blocking", "accountability"],
  })

  // Add specific events for key senators
  switch (lastName) {
    case "paul":
      events.push({
        id: `${senator.id}-statement-transparency-2025-09-10`,
        date: "2025-09-10",
        type: "statement",
        title: "Floor Speech Supporting Transparency",
        description:
          "Delivered passionate floor speech advocating for complete release of Epstein files: 'The American people deserve transparency and accountability. These files should be released in full.'",
        source: "Senate Floor Speech",
        sentiment: "support",
        verified: true,
        importance: "high",
        tags: ["transparency", "floor-speech", "accountability"],
      })
      break

    case "hawley":
      events.push({
        id: `${senator.id}-statement-courage-2025-09-10`,
        date: "2025-09-10",
        type: "statement",
        title: "Breaks with GOP Leadership",
        description:
          "Stated 'We need complete transparency on the Epstein case. No more cover-ups.' Showed political courage by crossing party lines to support transparency.",
        source: "Senate Vote Statement",
        sentiment: "support",
        verified: true,
        importance: "high",
        tags: ["courage", "party-lines", "transparency"],
      })
      break

    case "schumer":
      events.push({
        id: `${senator.id}-amendment-introduction-2025-09-10`,
        date: "2025-09-10",
        type: "statement",
        title: "Introduces Transparency Amendment",
        description:
          "Introduced S.Amdt.3849 to force DOJ release of all Epstein files: 'The American people have a right to know the full truth about Jeffrey Epstein's criminal network.'",
        source: "Press Release",
        sentiment: "support",
        verified: true,
        importance: "high",
        tags: ["amendment", "leadership", "transparency"],
      })
      break

    case "mcconnell":
      events.push({
        id: `${senator.id}-opposition-statement-2025-09-10`,
        date: "2025-09-10",
        type: "statement",
        title: "Opposes Transparency Measure",
        description:
          "Led Republican opposition to transparency amendment: 'We should trust the DOJ's judgment on what should be released,' effectively blocking full disclosure.",
        source: "Senate Floor Statement",
        sentiment: "oppose",
        verified: true,
        importance: "high",
        tags: ["opposition", "doj-trust", "blocking"],
      })
      break
  }

  return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

function SenatorProfilePage() {
  const params = Route.useParams() as { state: string; lastName: string }
  const { state, lastName } = params
  const { colorScheme } = useMantineColorScheme()

  // Find the senator in the data
  const senator = allCongressMembers.find(
    (member) =>
      member.chamber === "senate" &&
      member.state === state.toUpperCase() &&
      member.lastName.toLowerCase() === lastName.toLowerCase(),
  )

  if (!senator) {
    return (
      <>
        <SEO title="Senator Not Found" description="Senator profile not found" />
        <Container size="lg" py="xl">
          <Alert icon={<IconInfoCircle size={16} />} color="red" variant="light" title="Senator Not Found">
            No senator found for {state.toUpperCase()} with last name "{lastName}".
            <Anchor href="/congress" ml="sm">
              Return to Congress Tracker
            </Anchor>
          </Alert>
        </Container>
      </>
    )
  }

  const getPositionColor = (position: Position) => {
    switch (position) {
      case "support":
        return "green"
      case "oppose":
        return "red"
      case "unknown":
        return "gray"
      default:
        return "gray"
    }
  }

  const getPositionIcon = (position: Position) => {
    switch (position) {
      case "support":
        return <IconTrendingUp size={20} />
      case "oppose":
        return <IconTrendingDown size={20} />
      case "unknown":
        return <IconMinus size={20} />
    }
  }

  const getPositionText = (position: Position) => {
    switch (position) {
      case "support":
        return "Supporting Full Release"
      case "oppose":
        return "VOTED TO BLOCK TRANSPARENCY"
      case "unknown":
        return "Position Unknown - Ask Them"
    }
  }

  const getAccountabilityStatus = (position: Position) => {
    const voteDate = new Date("2025-09-10") // Senate vote date
    const today = new Date() // Current date (updates automatically)
    const daysSince = Math.floor((today.getTime() - voteDate.getTime()) / (1000 * 60 * 60 * 24))

    switch (position) {
      case "support":
        return {
          title: "TRANSPARENCY CHAMPION",
          icon: <IconShieldCheck size={20} />,
          status: "Voted FOR full file release (S.Amdt.3849)",
          details: "Stood up against party pressure for truth",
          action: "Thank them for their courage",
        }
      case "oppose":
        return {
          title: "ACCOUNTABILITY RECORD",
          icon: <IconAlertTriangle size={20} />,
          status: "Voted AGAINST transparency amendment",
          details: `${daysSince} days since blocking Epstein file release`,
          action: "Demand answers and accountability",
        }
      case "unknown":
        return {
          title: "POSITION UNKNOWN",
          icon: <IconQuestionMark size={20} />,
          status: "Has not taken public position",
          details: "Constituents deserve to know where they stand",
          action: "Ask them to support transparency",
        }
    }
  }

  const getPartyColor = (party: string) => {
    switch (party) {
      case "Republican":
        return "red"
      case "Democratic":
        return "blue"
      case "Independent":
        return "violet"
      default:
        return "gray"
    }
  }

  const positionText =
    senator.position === "support" ? "Supports Release" : senator.position === "oppose" ? "Opposes Release" : "Unknown"

  const personSchema = createPersonSchema({
    name: senator.name,
    jobTitle: `U.S. Senator for ${senator.state}`,
    affiliation:
      senator.party === "Republican"
        ? "Republican Party"
        : senator.party === "Democratic"
          ? "Democratic Party"
          : "Independent Party",
    telephone: senator.phone || undefined,
    email: senator.email || undefined,
    url: senator.website || undefined,
  })

  return (
    <>
      <SEO
        title={`Senator ${senator.name} - ${senator.state}`}
        description={`Contact Senator ${senator.name} (${senator.party}, ${senator.state}) about Epstein file transparency. Position: ${positionText}. Transparency Score: ${senator.transparencyScore}/100`}
        canonical={`https://rtefn.com/congress/senate/${state}/${lastName}`}
      />
      <StructuredData data={personSchema} />
      <div
        style={{
          background:
            colorScheme === "dark"
              ? "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)"
              : "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="lg" py="xl">
          {/* Back Button */}
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => (window.location.href = "/congress")}
            mb="lg"
          >
            Back to Congress Tracker
          </Button>

          <Grid>
            {/* Main Profile */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <Stack gap="lg">
                  {/* Header */}
                  <Group>
                    <Avatar
                      size={80}
                      radius="xl"
                      src={senator.photo}
                      alt={senator.name}
                      color={getPartyColor(senator.party)}
                    >
                      {senator.firstName.charAt(0)}
                      {senator.lastName.charAt(0)}
                    </Avatar>
                    <Box flex={1}>
                      <Group align="flex-start" justify="space-between">
                        <Box>
                          <Title
                            order={1}
                            size="2rem"
                            style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}
                          >
                            Senator {senator.name}
                          </Title>
                          <Text
                            size="lg"
                            style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b" }}
                          >
                            {senator.state}
                          </Text>
                        </Box>
                        <Badge variant="outline" size="lg" color="violet">
                          Senate
                        </Badge>
                      </Group>
                    </Box>
                  </Group>

                  {/* Position and Party */}
                  <Group gap="md">
                    <Badge
                      color={getPositionColor(senator.position)}
                      variant="filled"
                      size="lg"
                      leftSection={getPositionIcon(senator.position)}
                      style={{ fontWeight: senator.position === "oppose" ? 700 : 500 }}
                    >
                      {getPositionText(senator.position)}
                    </Badge>
                    <Badge color={getPartyColor(senator.party)} variant="light" size="lg">
                      {senator.party}
                    </Badge>
                  </Group>

                  {/* Accountability Status Alert */}
                  <Alert
                    color={senator.position === "support" ? "green" : senator.position === "oppose" ? "red" : "gray"}
                    variant="light"
                    icon={getAccountabilityStatus(senator.position).icon}
                    title={getAccountabilityStatus(senator.position).title}
                  >
                    <Stack gap="xs">
                      <Text size="sm" fw={500}>
                        {getAccountabilityStatus(senator.position).status}
                      </Text>
                      <Text size="sm">{getAccountabilityStatus(senator.position).details}</Text>
                    </Stack>
                  </Alert>

                  <Divider color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />

                  {/* Member Timeline */}
                  <MemberTimeline events={generateSenatorTimeline(senator)} memberName={senator.name} />

                  {/* Quotes */}
                  {senator.quotes.length > 0 && (
                    <>
                      <Divider color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />
                      <Box>
                        <Title order={3} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                          Public Statements
                        </Title>
                        <Stack gap="md">
                          {senator.quotes.map((quote, index) => (
                            <Card
                              key={index}
                              p="md"
                              style={{
                                background:
                                  colorScheme === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(255, 255, 255, 0.5)",
                                border:
                                  colorScheme === "dark"
                                    ? "1px solid rgba(255, 255, 255, 0.1)"
                                    : "1px solid rgba(0, 0, 0, 0.05)",
                              }}
                            >
                              <Group gap="sm" mb="xs">
                                <IconQuote
                                  size={16}
                                  style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                                />
                                <Text
                                  size="sm"
                                  fw={500}
                                  style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}
                                >
                                  {quote.source}
                                </Text>
                                <Text
                                  size="xs"
                                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.5)" : "#94a3b8" }}
                                >
                                  {new Date(quote.date).toLocaleDateString()}
                                </Text>
                              </Group>
                              <Text
                                style={{
                                  color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#374151",
                                  fontStyle: "italic",
                                  lineHeight: 1.6,
                                }}
                              >
                                "{quote.text}"
                              </Text>
                              {quote.context && (
                                <Text
                                  size="xs"
                                  mt="xs"
                                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.6)" : "#6b7280" }}
                                >
                                  Context: {quote.context}
                                </Text>
                              )}
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            {/* Contact Information */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="lg">
                {/* Contact Card */}
                <Card
                  shadow="lg"
                  radius="xl"
                  p="lg"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Title order={4} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    Contact Information
                  </Title>
                  <Stack gap="sm">
                    {senator.phone && (
                      <Group gap="sm">
                        <IconPhone size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={`tel:${senator.phone}`}
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          {senator.phone}
                        </Anchor>
                      </Group>
                    )}
                    {senator.email && (
                      <Group gap="sm">
                        <IconMail size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={`mailto:${senator.email}`}
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          Email
                        </Anchor>
                      </Group>
                    )}
                    {senator.website && (
                      <Group gap="sm">
                        <IconExternalLink size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={senator.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          Official Website
                        </Anchor>
                      </Group>
                    )}
                    {senator.twitter && (
                      <Group gap="sm">
                        <IconBrandTwitter size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={`https://twitter.com/${senator.twitter.replace("@", "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          {senator.twitter}
                        </Anchor>
                      </Group>
                    )}
                  </Stack>
                </Card>

                {/* Election Info */}
                <Card
                  shadow="lg"
                  radius="xl"
                  p="lg"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Title order={4} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    Election Information
                  </Title>
                  <Group gap="sm" mb="md">
                    <IconCalendar size={16} style={{ color: colorScheme === "dark" ? "#8b5cf6" : "#7c3aed" }} />
                    <Text style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#374151" }}>
                      Next Election: {senator.nextElection}
                    </Text>
                  </Group>
                  <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#6b7280" }}>
                    6-year Senate term
                  </Text>
                </Card>

                {/* Committees */}
                {senator.committees && senator.committees.length > 0 && (
                  <Card
                    shadow="lg"
                    radius="xl"
                    p="lg"
                    style={{
                      background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(20px)",
                      border:
                        colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <Title order={4} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                      Committee Assignments
                    </Title>
                    <Stack gap="xs">
                      {senator.committees.map((committee, index) => (
                        <Badge key={index} variant="light" color="blue">
                          {committee}
                        </Badge>
                      ))}
                    </Stack>
                  </Card>
                )}

                {/* Accountability Status Card */}
                <Card
                  shadow="lg"
                  radius="xl"
                  p="lg"
                  style={{
                    background:
                      colorScheme === "dark"
                        ? senator.position === "support"
                          ? "rgba(16, 185, 129, 0.1)"
                          : senator.position === "oppose"
                            ? "rgba(239, 68, 68, 0.1)"
                            : "rgba(107, 114, 128, 0.1)"
                        : senator.position === "support"
                          ? "rgba(16, 185, 129, 0.05)"
                          : senator.position === "oppose"
                            ? "rgba(239, 68, 68, 0.05)"
                            : "rgba(107, 114, 128, 0.05)",
                    border:
                      senator.position === "support"
                        ? "1px solid rgba(16, 185, 129, 0.3)"
                        : senator.position === "oppose"
                          ? "1px solid rgba(239, 68, 68, 0.3)"
                          : "1px solid rgba(107, 114, 128, 0.2)",
                  }}
                >
                  <Group gap="sm" mb="sm">
                    {getAccountabilityStatus(senator.position).icon}
                    <Title
                      order={4}
                      style={{
                        color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                        fontWeight: senator.position === "oppose" ? 700 : 600,
                      }}
                    >
                      {getAccountabilityStatus(senator.position).title}
                    </Title>
                  </Group>
                  <Stack gap="xs">
                    <Group gap="xs" wrap="nowrap">
                      <Text
                        size="sm"
                        style={{
                          color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937",
                          fontWeight: 500,
                        }}
                      >
                        ├─
                      </Text>
                      <Text
                        size="sm"
                        style={{
                          color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937",
                          fontWeight: 500,
                        }}
                      >
                        {getAccountabilityStatus(senator.position).status}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Text
                        size="sm"
                        style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937" }}
                      >
                        ├─
                      </Text>
                      <Text
                        size="sm"
                        style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937" }}
                      >
                        {getAccountabilityStatus(senator.position).details}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Text
                        size="sm"
                        style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937" }}
                      >
                        └─
                      </Text>
                      <Text
                        size="sm"
                        fw={500}
                        style={{
                          color:
                            senator.position === "support"
                              ? "#10b981"
                              : senator.position === "oppose"
                                ? "#ef4444"
                                : "#6b7280",
                        }}
                      >
                        {getAccountabilityStatus(senator.position).action}
                      </Text>
                    </Group>
                  </Stack>
                </Card>

                {/* Action Button */}
                <Button
                  size="lg"
                  fullWidth
                  style={{
                    background:
                      senator.position === "support"
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : senator.position === "oppose"
                          ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                          : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                    border: "none",
                    fontWeight: senator.position === "oppose" ? 700 : 600,
                  }}
                  component="a"
                  href="/action"
                >
                  {senator.position === "support"
                    ? "Thank Them for Courage"
                    : senator.position === "oppose"
                      ? "Demand They Stop Blocking"
                      : "Ask Their Position"}
                </Button>
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>
      </div>
    </>
  )
}
